import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Settings2,
  Loader2,
  Save,
  ChevronDown,
  ChevronRight,
  Monitor,
  Mail,
  Smartphone,
  UserCheck,
  Building2,
  Users,
  Info,
  RefreshCw,
  BellOff,
} from 'lucide-react';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { notificationService } from '@/services/notification.service';
import {
  SystemNotificationConfigItem,
  SystemNotificationConfigList,
  SystemNotificationModuleGroup,
  SystemNotificationRecipientMode,
  SystemNotificationDepartmentRole,
} from '@/types/notification.types';
import toast from 'react-hot-toast';
import { DepartmentRolePickerModal } from './DepartmentRolePickerModal';
import { UserPickerModal } from './UserPickerModal';

const CHANNELS = [
  { key: 'in_app' as const, label: 'In-App', icon: Monitor },
  { key: 'email' as const, label: 'Email', icon: Mail },
  { key: 'push' as const, label: 'Push', icon: Smartphone },
];

const RECIPIENT_MODES = [
  {
    key: SystemNotificationRecipientMode.TRIGGER_USER,
    label: 'Default',
    hint: 'The user who performed the operation',
    icon: UserCheck,
    ready: true,
  },
  {
    key: SystemNotificationRecipientMode.DEPARTMENT_ROLE,
    label: 'Department & Role',
    hint: 'Everyone holding the selected roles',
    icon: Building2,
    ready: true,
  },
  {
    key: SystemNotificationRecipientMode.CUSTOM_USERS,
    label: 'Specific Users',
    hint: 'A hand-picked list of active users',
    icon: Users,
    ready: true,
  },
];

// A row is savable only when the backend validation for `enabled: true` would
// pass, so the UI blocks the request instead of surfacing a 400.
const getBlockingReason = (event: SystemNotificationConfigItem): string | null => {
  if (!event.enabled) return null;

  const { in_app, email, push } = event.channels;
  if (!in_app && !email && !push) return 'Select at least one channel';

  if (event.recipients.modes.length === 0) return 'Select at least one recipient option';

  if (
    event.recipients.modes.includes(SystemNotificationRecipientMode.DEPARTMENT_ROLE) &&
    event.recipients.department_roles.length === 0
  ) {
    return 'Select at least one department';
  }

  if (
    event.recipients.modes.includes(SystemNotificationRecipientMode.CUSTOM_USERS) &&
    event.recipients.user_ids.length === 0
  ) {
    return 'Select at least one user';
  }

  return null;
};

const snapshotOf = (event: SystemNotificationConfigItem) =>
  JSON.stringify({
    enabled: event.enabled,
    channels: event.channels,
    recipients: event.recipients,
  });

function CardHeader() {
  return (
    <div className="px-6 py-4 border-b border-gray-100">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-100 rounded-xl">
          <Settings2 className="w-6 h-6 text-primary-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            System Notification Configuration
          </h2>
          <p className="text-sm text-gray-500">
            Choose which operations notify people, on which channels, and who receives them
          </p>
        </div>
      </div>
    </div>
  );
}

export function SystemNotificationConfig() {
  const [data, setData] = useState<SystemNotificationConfigList | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [departmentPickerFor, setDepartmentPickerFor] = useState<string | null>(null);
  const [userPickerFor, setUserPickerFor] = useState<string | null>(null);

  // Snapshot of the last server state, used to tell which rows are dirty.
  const [baseline, setBaseline] = useState<Record<string, string>>({});

  const buildBaseline = useCallback((list: SystemNotificationConfigList) => {
    const next: Record<string, string> = {};
    for (const group of list.modules) {
      for (const event of group.events) {
        next[event.event_type] = snapshotOf(event);
      }
    }
    return next;
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    const list = await notificationService.getSystemNotificationConfigs();
    if (list) {
      setData(list);
      setBaseline(buildBaseline(list));
      setLoadFailed(false);
    } else {
      setData(null);
      setLoadFailed(true);
    }
    setIsLoading(false);
  }, [buildBaseline]);

  useEffect(() => {
    load();
  }, [load]);

  const patchEvent = (
    event_type: string,
    patch: (event: SystemNotificationConfigItem) => SystemNotificationConfigItem
  ) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        modules: prev.modules.map((group) => ({
          ...group,
          events: group.events.map((event) =>
            event.event_type === event_type ? patch(event) : event
          ),
        })),
      };
    });
  };

  const isDirty = useCallback(
    (event: SystemNotificationConfigItem) => baseline[event.event_type] !== snapshotOf(event),
    [baseline]
  );

  const allEvents = useMemo(() => data?.modules.flatMap((group) => group.events) ?? [], [data]);

  const dirtyEvents = useMemo(() => allEvents.filter(isDirty), [allEvents, isDirty]);

  const departmentPickerEvent = useMemo(
    () => allEvents.find((event) => event.event_type === departmentPickerFor) ?? null,
    [allEvents, departmentPickerFor]
  );

  const userPickerEvent = useMemo(
    () => allEvents.find((event) => event.event_type === userPickerFor) ?? null,
    [allEvents, userPickerFor]
  );

  const handleToggleEnabled = (event: SystemNotificationConfigItem) => {
    patchEvent(event.event_type, (prev) => {
      const enabled = !prev.enabled;
      // Turning an untouched event on with nothing selected is a dead end,
      // so seed the defaults the admin would pick anyway.
      if (enabled && !prev.channels.in_app && !prev.channels.email && !prev.channels.push) {
        return {
          ...prev,
          enabled,
          channels: { ...prev.channels, in_app: true },
          recipients: {
            ...prev.recipients,
            modes: prev.recipients.modes.length
              ? prev.recipients.modes
              : [SystemNotificationRecipientMode.TRIGGER_USER],
          },
        };
      }
      return { ...prev, enabled };
    });
  };

  const handleToggleChannel = (
    event: SystemNotificationConfigItem,
    channel: 'in_app' | 'email' | 'push'
  ) => {
    patchEvent(event.event_type, (prev) => ({
      ...prev,
      channels: { ...prev.channels, [channel]: !prev.channels[channel] },
    }));
  };

  const handleToggleMode = (
    event: SystemNotificationConfigItem,
    mode: SystemNotificationRecipientMode
  ) => {
    patchEvent(event.event_type, (prev) => {
      const has = prev.recipients.modes.includes(mode);
      return {
        ...prev,
        recipients: {
          ...prev.recipients,
          modes: has
            ? prev.recipients.modes.filter((m) => m !== mode)
            : [...prev.recipients.modes, mode],
        },
      };
    });

    // Opening the picker straight away saves a second click, since the mode is
    // unusable until something is chosen.
    if (!event.recipients.modes.includes(mode)) {
      if (mode === SystemNotificationRecipientMode.DEPARTMENT_ROLE) {
        setDepartmentPickerFor(event.event_type);
      } else if (mode === SystemNotificationRecipientMode.CUSTOM_USERS) {
        setUserPickerFor(event.event_type);
      }
    }
  };

  const handleSaveDepartmentRoles = (
    event_type: string,
    department_roles: SystemNotificationDepartmentRole[]
  ) => {
    patchEvent(event_type, (prev) => ({
      ...prev,
      recipients: { ...prev.recipients, department_roles },
    }));
  };

  const handleSaveUserIds = (event_type: string, user_ids: string[]) => {
    patchEvent(event_type, (prev) => ({
      ...prev,
      recipients: { ...prev.recipients, user_ids },
    }));
  };

  const handleSave = async () => {
    if (dirtyEvents.length === 0) return;

    // The bulk endpoint is not transactional, so reject the whole batch here
    // rather than letting a bad item land halfway through.
    const blocked = dirtyEvents
      .map((event) => ({ event, reason: getBlockingReason(event) }))
      .filter((entry) => entry.reason);

    if (blocked.length > 0) {
      toast.error(`${blocked[0].event.label}: ${blocked[0].reason}`);
      return;
    }

    setIsSaving(true);
    const result = await notificationService.bulkUpsertSystemNotificationConfigs(
      dirtyEvents.map((event) => ({
        event_type: event.event_type,
        enabled: event.enabled,
        channels: event.channels,
        recipients: event.recipients,
      }))
    );
    setIsSaving(false);

    if (!result.success) {
      toast.error(result.error || 'Failed to save configuration');
      return;
    }

    if (result.data) {
      setData(result.data);
      setBaseline(buildBaseline(result.data));
    }
    toast.success('System notification configuration saved');
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <SkeletonLoader type="custom" className="w-10 h-10 rounded-xl" />
            <div className="space-y-2">
              <SkeletonLoader type="text" className="h-5 w-64" />
              <SkeletonLoader type="text" className="h-3 w-80" />
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50">
                <SkeletonLoader type="text" className="h-4 w-32" />
              </div>
              <div className="p-4 space-y-3">
                <SkeletonLoader type="text" className="h-4 w-48" />
                <SkeletonLoader type="text" className="h-3 w-64" />
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
          <SkeletonLoader type="custom" className="h-10 w-40 rounded-lg" />
        </div>
      </div>
    );
  }

  const modules = data?.modules ?? [];
  const isEmpty = modules.length === 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <CardHeader />

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <BellOff className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {loadFailed ? 'Configuration unavailable' : 'No configurable operations'}
          </h3>
          <p className="text-gray-500 text-center max-w-sm">
            {loadFailed
              ? "We couldn't load the system notification configuration. Try again in a moment."
              : 'There are no operations available to configure yet. They will appear here once added.'}
          </p>
          {loadFailed && (
            <button
              onClick={load}
              className="mt-5 flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="p-6 space-y-6">
            {/* Pending-API notice */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-900 leading-relaxed">
                <span className="font-medium">
                  Department and specific-user targeting is coming soon.
                </span>{' '}
                Those options stay disabled until the department, role, and user selection APIs are
                available. <strong>Default</strong> targeting notifies whoever performed the
                operation and works today.
              </p>
            </div>

            <section>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Operations
                </h3>
                <span className="text-sm text-gray-500">
                  {data?.total_enabled ?? 0} of {data?.total_events ?? 0} enabled
                </span>
              </div>

              {/* Module groups, rendered in catalog order */}
              <div className="space-y-4">
                {modules.map((group) => (
                  <ModuleGroup
                    key={group.module}
                    group={group}
                    isCollapsed={!!collapsed[group.module]}
                    onToggleCollapse={() =>
                      setCollapsed((prev) => ({ ...prev, [group.module]: !prev[group.module] }))
                    }
                    isDirty={isDirty}
                    onToggleEnabled={handleToggleEnabled}
                    onToggleChannel={handleToggleChannel}
                    onToggleMode={handleToggleMode}
                    onOpenDepartmentPicker={setDepartmentPickerFor}
                    onOpenUserPicker={setUserPickerFor}
                  />
                ))}
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-gray-500">
                {dirtyEvents.length > 0
                  ? `${dirtyEvents.length} unsaved change${dirtyEvents.length > 1 ? 's' : ''}`
                  : 'All changes saved'}
              </p>
              <button
                onClick={handleSave}
                disabled={dirtyEvents.length === 0 || isSaving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {departmentPickerEvent && (
        <DepartmentRolePickerModal
          isOpen
          onClose={() => setDepartmentPickerFor(null)}
          eventLabel={departmentPickerEvent.label}
          value={departmentPickerEvent.recipients.department_roles}
          onSave={(department_roles) =>
            handleSaveDepartmentRoles(departmentPickerEvent.event_type, department_roles)
          }
        />
      )}

      {userPickerEvent && (
        <UserPickerModal
          isOpen
          onClose={() => setUserPickerFor(null)}
          eventLabel={userPickerEvent.label}
          value={userPickerEvent.recipients.user_ids}
          onSave={(user_ids) => handleSaveUserIds(userPickerEvent.event_type, user_ids)}
        />
      )}
    </div>
  );
}

interface ModuleGroupProps {
  group: SystemNotificationModuleGroup;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isDirty: (event: SystemNotificationConfigItem) => boolean;
  onToggleEnabled: (event: SystemNotificationConfigItem) => void;
  onToggleChannel: (
    event: SystemNotificationConfigItem,
    channel: 'in_app' | 'email' | 'push'
  ) => void;
  onToggleMode: (
    event: SystemNotificationConfigItem,
    mode: SystemNotificationRecipientMode
  ) => void;
  onOpenDepartmentPicker: (event_type: string) => void;
  onOpenUserPicker: (event_type: string) => void;
}

function ModuleGroup({
  group,
  isCollapsed,
  onToggleCollapse,
  isDirty,
  onToggleEnabled,
  onToggleChannel,
  onToggleMode,
  onOpenDepartmentPicker,
  onOpenUserPicker,
}: ModuleGroupProps) {
  const Chevron = isCollapsed ? ChevronRight : ChevronDown;

  return (
    <section className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggleCollapse}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Chevron className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">{group.label}</span>
          <span className="text-xs text-gray-500">
            ({group.events.length} operation{group.events.length > 1 ? 's' : ''})
          </span>
        </div>
        {group.enabled_count > 0 && (
          <span className="px-2 py-0.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full">
            {group.enabled_count} enabled
          </span>
        )}
      </button>

      {!isCollapsed &&
        (group.events.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-gray-500">No operations in this module yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {group.events.map((event) => (
              <EventRow
                key={event.event_type}
                event={event}
                isDirty={isDirty(event)}
                onToggleEnabled={onToggleEnabled}
                onToggleChannel={onToggleChannel}
                onToggleMode={onToggleMode}
                onOpenDepartmentPicker={onOpenDepartmentPicker}
                onOpenUserPicker={onOpenUserPicker}
              />
            ))}
          </div>
        ))}
    </section>
  );
}

interface EventRowProps {
  event: SystemNotificationConfigItem;
  isDirty: boolean;
  onToggleEnabled: (event: SystemNotificationConfigItem) => void;
  onToggleChannel: (
    event: SystemNotificationConfigItem,
    channel: 'in_app' | 'email' | 'push'
  ) => void;
  onToggleMode: (
    event: SystemNotificationConfigItem,
    mode: SystemNotificationRecipientMode
  ) => void;
  onOpenDepartmentPicker: (event_type: string) => void;
  onOpenUserPicker: (event_type: string) => void;
}

function EventRow({
  event,
  isDirty,
  onToggleEnabled,
  onToggleChannel,
  onToggleMode,
  onOpenDepartmentPicker,
  onOpenUserPicker,
}: EventRowProps) {
  const blocked = getBlockingReason(event);

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900">{event.label}</p>
            {isDirty && (
              <span className="px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
                Unsaved
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{event.description}</p>
        </div>

        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            checked={event.enabled}
            onChange={() => onToggleEnabled(event)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      {event.enabled && (
        <div className="mt-4 space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Channels
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {CHANNELS.map((channel) => {
                const Icon = channel.icon;
                const active = event.channels[channel.key];
                return (
                  <button
                    key={channel.key}
                    onClick={() => onToggleChannel(event, channel.key)}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                      active
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {channel.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Recipients
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {RECIPIENT_MODES.map((mode) => {
                const Icon = mode.icon;
                const active = event.recipients.modes.includes(mode.key);
                return (
                  <button
                    key={mode.key}
                    onClick={() => mode.ready && onToggleMode(event, mode.key)}
                    disabled={!mode.ready}
                    title={mode.hint}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                      !mode.ready
                        ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                        : active
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selection summaries, only for modes that need a choice */}
          {event.recipients.modes.includes(SystemNotificationRecipientMode.DEPARTMENT_ROLE) && (
            <SelectionSummary
              icon={Building2}
              label="Roles"
              count={event.recipients.department_roles.reduce(
                (total, entry) => total + entry.role_keys.length,
                0
              )}
              emptyText="No roles selected"
              onEdit={() => onOpenDepartmentPicker(event.event_type)}
            />
          )}

          {event.recipients.modes.includes(SystemNotificationRecipientMode.CUSTOM_USERS) && (
            <SelectionSummary
              icon={Users}
              label="Users"
              count={event.recipients.user_ids.length}
              emptyText="No users selected"
              onEdit={() => onOpenUserPicker(event.event_type)}
            />
          )}

          {blocked && <p className="text-xs text-red-600">{blocked}</p>}
        </div>
      )}
    </div>
  );
}

interface SelectionSummaryProps {
  icon: typeof Building2;
  label: string;
  count: number;
  emptyText: string;
  onEdit: () => void;
}

function SelectionSummary({ icon: Icon, label, count, emptyText, onEdit }: SelectionSummaryProps) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="text-sm text-gray-700 truncate">
          {count > 0 ? (
            <>
              <span className="font-medium">{count}</span> {label.toLowerCase()} selected
            </>
          ) : (
            <span className="text-gray-500">{emptyText}</span>
          )}
        </span>
      </div>
      <button
        onClick={onEdit}
        className="text-sm font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
      >
        {count > 0 ? 'Edit' : 'Select'}
      </button>
    </div>
  );
}

export default SystemNotificationConfig;
